import { OptionDto, StorySectionDto } from "../core/dtos";
import Select from "../core/selector";
import { translate } from "../core/translate";
import { fromHTML } from "../core/util";

export class ReadPage {
  private readonly buttonSection = Select.byId("button-container");
  private readonly errorMessage = Select.byId("error-message");
  private readonly loadingSpinner = Select.byId("loading-indicator");
  private readonly regenerateButton =
    Select.byId<HTMLButtonElement>("regenerate-btn");
  private readonly optionContainer = Select.byId("option-container");
  private readonly textSectionContainer = Select.byId(
    "text-section-container"
  );

  private activeSection: HTMLElement;
  private activeParagraph: HTMLParagraphElement;

  public onOptionButtonClicked(callback: (key: number) => void): void {
    this.optionContainer.addEventListener("click", (e) => {
      const targetButton = e.target as HTMLButtonElement;
      if (!targetButton.classList.contains("btn") || targetButton.disabled) {
        return;
      }

      const key = targetButton.dataset.choiceKey;
      callback(parseInt(key!));
    });
  }

  public onRegenerateButtonClicked(callback: () => void): void {
    this.regenerateButton.addEventListener("click", callback);
  }

  public replaceLastSection(sectionDto: StorySectionDto): void {
    const lastParagraph = this.getLastSection();

    const section = this.createTextSection(sectionDto.text);
    lastParagraph.innerHTML = section.innerHTML;

    this.updateChoices(sectionDto.options);
  }

  public insertSection(sectionDto: StorySectionDto): void {
    const section = this.createTextSection(sectionDto.text);

    this.textSectionContainer.appendChild(document.createElement("hr"));
    this.textSectionContainer.append(section);

    this.updateChoices(sectionDto.options);
  }

  public removeLastSection(): void {
    this.textSectionContainer.querySelector("section:last-child").remove();
    this.textSectionContainer.querySelector("hr:last-child").remove();
  }

  public updateChoices(options: OptionDto[]) {
    this.optionContainer.innerHTML = this.generateOptionsHtml(options);
  }

  //#region build section while streaming text

  public insertEmptySection(): void {
    this.activeSection = this.createTextSection("");
    this.activeParagraph = this.activeSection
      .firstElementChild as HTMLParagraphElement;

    this.textSectionContainer.appendChild(document.createElement("hr"));
    this.textSectionContainer.append(this.activeSection);
  }

  public appendText(text: string): void {
    this.activeParagraph.innerText += text;
  }

  public appendNewParagraph(text: string): void {
    this.activeParagraph = this.generateParagraphElement(text);
    this.activeSection.appendChild(this.activeParagraph);
  }

  //#endregion

  //#region state display

  public showLoadingSpinner(): void {
    this.loadingSpinner.classList.remove("d-none");
  }

  public hideLoadingSpinner(): void {
    this.loadingSpinner.classList.add("d-none");
  }

  public hideAllButtons(): void {
    this.buttonSection.classList.add("d-none");
  }

  public showAllButtons(): void {
    this.buttonSection.classList.remove("d-none");
  }
  
  public enableAllButtons() {
    const buttons = this.optionContainer.querySelectorAll("button");
    [...buttons, this.regenerateButton].forEach(
      (btn) => (btn.disabled = false)
    );
  }

  public showButtonsAsLoading() {
    const buttons = this.optionContainer.querySelectorAll("button");
    buttons.forEach((btn) => {
      btn.innerText = "...";
    });
  }

  public disableAllButtons() {
    const buttons = this.optionContainer.querySelectorAll("button");
    [...buttons, this.regenerateButton].forEach((btn) => (btn.disabled = true));
  }

  public showPromptLimitReachedPopover(show: boolean): void {
    const container = Select.byId("no-more-tokens-popover");

    if (show) {
      container.classList.remove("d-none");
      return;
    }

    container.classList.add("d-none");
  }

  //#endregion

  public showError(errorMessage: string): void {
    this.errorMessage.innerHTML = /*html*/`<div class="alert alert-warning" role="alert">
      ${translate(errorMessage)}
    </div>`;
  }

  public clearError(): void {
    this.errorMessage.innerHTML = "";
  }

  public focusLastSection() {
    const lastParagraph = this.getLastSection();
    lastParagraph.scrollIntoView();
    lastParagraph.classList.add("highlight");

    setTimeout(() => {
      lastParagraph.classList.remove("highlight");
    }, 1000);
  }

  public storyIsNew(): boolean {
    return this.textSectionContainer.children.length === 1;
  }

  private getLastSection() {
    return this.textSectionContainer.querySelector("section:last-child");
  }

  private createTextSection(textSection: string) {
    const section = document.createElement("section");
    section.innerHTML = textSection
      .split("\n\n")
      .map((text) => this.generateParagraphsHtml(text))
      .join("");
    return section;
  }

  private generateParagraphsHtml(text: string): string {
    return /*html*/ `<p class="mb-0 lead story-paragraph story-text">${text}</p>`;
  }

  private generateParagraphElement(text: string): HTMLParagraphElement {
    return fromHTML(this.generateParagraphsHtml(text));
  }

  private generateOptionsHtml(options: OptionDto[]): string {
    return /*html*/ `
      <div class="row mt-4 justify-content-center">
        ${options
          .map(
            (opt) => /*html*/ `
            <div class="col-12 mb-2 col-md-6 col-lg-4">
              <button class="w-100 h-100 text-center btn btn-primary choice-btn" data-choice-key="${opt.key}">${opt.text}</button>
            </div>`
          )
          .join("")}
      </div>
    `;
  }
}
